import { MAX_RESUME_BYTES } from '@shared/resume';

export function ResumeUpload({ file, onChange, error, onError, disabled }: {
  file: File | null;
  onChange: (file: File | null) => void;
  error?: string;
  onError: (message: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="rounded-xl border border-dashed border-brand-200 bg-white p-5">
      <label htmlFor="resume" className="block text-sm font-medium">Resume <span className="text-red-600" aria-hidden="true">*</span></label>
      <p id="resume-help" className="mt-2 text-sm text-ink/70">PDF, up to 5 MB. A portfolio link is welcome too.</p>
      <input id="resume" type="file" required accept=".pdf,application/pdf" disabled={disabled}
        aria-invalid={!!error} aria-describedby={`resume-help${error ? ' resume-error' : ''}`}
        className="mt-4 block w-full min-w-0 text-sm file:mr-3 file:rounded-full file:border-0 file:bg-brand-50 file:px-4 file:py-3 file:font-medium file:text-brand"
        onChange={(event) => {
          const candidate = event.target.files?.[0];
          if (!candidate) return;
          if (!/\.pdf$/i.test(candidate.name) || candidate.size === 0 || candidate.size > MAX_RESUME_BYTES) {
            event.target.value = '';
            onChange(null);
            onError('Please choose a non-empty PDF no larger than 5 MB.');
            return;
          }
          onChange(candidate);
          onError('');
        }} />
      {(file || error) && <div className="mt-3 flex items-center justify-between gap-3 text-sm">
        <span className="min-w-0 break-all">{file ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB` : 'No resume attached'}</span>
        <button type="button" disabled={disabled} className="min-h-11 shrink-0 text-brand underline underline-offset-4" onClick={() => {
          onChange(null);
          onError('');
          const input = document.getElementById('resume') as HTMLInputElement | null;
          if (input) input.value = '';
        }}>Remove</button>
      </div>}
      {error && <p id="resume-error" role="alert" className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export function readResume(file: File): Promise<{ filename: string; content: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Your resume could not be read. Please select it again.'));
    reader.onload = () => resolve({ filename: file.name, content: String(reader.result).split(',')[1] });
    reader.readAsDataURL(file);
  });
}
